import React from 'react';
import heroImage from '../assets/article-biosimilars-patient-guide.png';
import articleBodyImage from '../assets/article-biosimilars1.png';
import articleClinicalImage from '../assets/article-biosimilars2.png';

const ArticleBiosimilarsPatientGuide = () => {
  return (
    <div className="bg-white pb-16">
      <section
        className="relative isolate min-h-[38vh] w-full overflow-hidden sm:min-h-[40vh]"
        aria-labelledby="bio-article-hero-heading"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-950/55 via-slate-950/35 to-slate-950/60"
          aria-hidden
        />
        <div className="relative z-10 mx-auto flex min-h-[38vh] max-w-6xl flex-col items-center justify-center px-4 py-12 sm:min-h-[40vh] sm:px-6 sm:py-14 lg:px-8">
          <div className="w-full max-w-3xl text-center">
            <h1
              id="bio-article-hero-heading"
              className="text-pretty text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl"
            >
              What patients should know about biosimilars
            </h1>
            <p className="mt-4 text-sm text-slate-100/95 sm:text-base">
              <span className="text-slate-100">By Dr. Neha Kapoor, MD</span>
              <span className="mx-2 text-slate-300/90" aria-hidden>
                ·
              </span>
              <time dateTime="2026-04-21" className="tabular-nums text-slate-100/90">
                April 21, 2026
              </time>
            </p>
          </div>
        </div>
      </section>

      <section
        className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12"
        aria-labelledby="bio-article-body-heading"
      >
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)] md:items-stretch md:gap-8 lg:gap-10">
          <div className="min-h-0 w-full md:h-full">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl shadow-lg ring-1 ring-slate-200/80 md:aspect-auto md:h-full md:min-h-0">
              <img
                src={articleBodyImage}
                alt="Pharmacy shelves with labelled medicine jars and bottles, illustrating biologic and biosimilar products"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
          <div className="min-h-0 min-w-0 space-y-5 text-base leading-relaxed text-slate-600">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-brand-700">Definitions and access</p>
              <h2
                id="bio-article-body-heading"
                className="mt-2 text-pretty text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl"
              >
                Understanding biosimilars
              </h2>
            </div>
            <p>
              A biosimilar is a biological medicine highly similar to an already approved reference product, with no
              clinically meaningful differences in safety or effectiveness expected from rigorous comparability data.
            </p>
            <p>
              Patients often ask about switching, immunogenicity, and cost. We separate what regulators require from what
              still varies by country, payer, and clinical setting.
            </p>
            <p>
              If your medicine is given in a clinic or hospital, staff will usually document the product name and batch
              so any reaction can be traced—whether you receive the reference biologic or a biosimilar. At-home
              injectables follow the same principle: keep the packaging until you are comfortable with the device, and
              mention any past reactions when doses change.
            </p>
            <p>
              Coverage rules differ widely. A biosimilar may reduce your out-of-pocket costs or require a new prior
              authorisation; in other cases your plan may prefer one supplier for stability. It is reasonable to ask what
              was prescribed, what the pharmacy dispensed, and who to call if the appearance of the device or vial looks
              different from what you expect.
            </p>
          </div>
        </div>
      </section>

      <section
        className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12"
        aria-labelledby="bio-article-safety-heading"
      >
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] md:items-stretch md:gap-8 lg:gap-10">
          <div className="min-h-0 min-w-0 space-y-5 text-base leading-relaxed text-slate-600">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-brand-700">Safety and technique</p>
              <h2
                id="bio-article-safety-heading"
                className="mt-2 text-pretty text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl"
              >
                Side effects, monitoring, and when to speak up
              </h2>
            </div>
            <p>
              Approval packages for biosimilars include immunogenicity comparisons to the reference product. That does
              not remove the need to report anything new after a switch—rashes, fevers, shortness of breath, or a flare
              of your underlying condition should go to your clinical team using the same pathways you would use for any
              biologic.
            </p>
            <p>
              Infusion or injection technique, storage, and device training can differ slightly between manufacturers
              even when the active drug is the same. If something feels harder to use, ask for a refresher rather than
              skipping doses; often a nurse educator or specialty pharmacist can troubleshoot quickly.
            </p>
            <p>
              Keep a simple note of the product name you last received and the date. It helps if you see more than one
              specialist, travel, or need urgent care, where records may not yet show the latest dispense.
            </p>
          </div>
          <div className="min-h-0 w-full md:h-full">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl shadow-lg ring-1 ring-slate-200/80 md:aspect-auto md:h-full md:min-h-0">
              <img
                src={articleClinicalImage}
                alt="Open orange prescription bottle with pale green and white capsules spilled on a light surface"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticleBiosimilarsPatientGuide;
